import { useState, useRef, useEffect } from 'react';

/**
 * Custom hook for managing action states with loading, success, and error handling
 * @param {Object} options - Configuration options
 * @param {number} options.successDuration - Duration to show success notification (ms)
 * @param {number} options.errorDuration - Duration to show error notification (ms)
 * @returns {Object} Action state and handlers
 */
const useActionState = (options = {}) => {
  const { successDuration = 3000, errorDuration = 5000 } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const timeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const execute = async (
    actionPromise,
    { successMessage = 'Action completed successfully', errorMessage = 'Action failed', onSuccess, onError } = {}
  ) => {
    if (isLoading) return;

    setIsLoading(true);
    setNotification({ show: true, loading: true });

    try {
      const result = await actionPromise;

      setNotification({
        show: true,
        loading: false,
        success: true,
        message: successMessage,
      });

      if (onSuccess) {
        onSuccess(result);
      }

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setNotification(null);
      }, successDuration);
    } catch (error) {
      const errorMsg = error.response?.data?.message || errorMessage;

      setNotification({
        show: true,
        loading: false,
        success: false,
        message: errorMsg,
      });

      if (onError) {
        onError(error);
      }

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setNotification(null);
      }, errorDuration);

      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setIsLoading(false);
    setNotification(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  return {
    isLoading,
    notification,
    execute,
    reset,
  };
};

export default useActionState;
